import {
  AgentChatThreadId,
  AppSessionId,
  Attribution,
  CollectionId,
  ContextChangesetId,
  ContextSuggestionDataWarehouseUpdateId,
  ContextSuggestionFileUpdateId,
  ContextSuggestionIssueId,
  ContextSuggestionIssueStatus,
  CustomerEvalSuiteRunId,
  DataBrowserIdType,
  DataBrowserRecommendedIdType,
  DataBrowserRecommendedIdTypeLiteral,
  DataBrowserSemanticIdType,
  DataConnectionId,
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  ExploreId,
  ExploreSessionStateId,
  HexFilter,
  HexId,
  HexLocator,
  HexVersionString,
  NoAttribution,
  OrgId,
  PlainCodeProjectId,
  PromoCode,
  SemanticDatasetId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticProjectVersionId,
  SemanticViewName,
  SpecialVersionType,
  TemplateId,
  ThreadLocator,
  assertNever,
  decodeUrlObject,
  encodeHash,
  encodeQueryParams,
  encodeUrlObject,
  getNormalEnum,
  withAttribution,
} from "@hex/common";
import Slugger from "github-slugger";
import { identity } from "lodash";
import { useCallback } from "react";
import {
  Location,
  NavigateFunction,
  createPath,
  useLocation,
  useParams,
} from "react-router-dom";
import { Literal, Static, Union } from "runtypes";
import { OptionalKeysOf, RequiredKeysOf } from "type-fest";

import { ContextWorkbenchPublish } from "../components/context-workbench/redux/contextWorkbenchPublish";
import {
  OnboardingDialogContext,
  OnboardingDialogId,
} from "../components/tour/dialogs";
import type { TourId } from "../components/tour/tours.js";
import { ORG_ID, getUrlOrg } from "../orgs";
import {
  EditViewType,
  serializeProjectViews,
} from "../util/projectViewTypes.js";

export const PRESERVE_APP_STATE = "preserveAppState";
export const SAVED_VIEW_QUERY_PARAM = "view";
export const DISABLE_APP_SESSION_REUSE_QUERY_PARAM = "newSession";
export const RUN_STATS_SESSION_PARAM = "runSessionId";

export type ViewType = "ui" | "logic" | "split" | "component" | "debug";

export type HomeTab =
  | "home"
  | "collections"
  | "components"
  | "all"
  | "you"
  | "library"
  | "sharedWithYou"
  | "sharedWithOrg"
  | "trash"
  | "archive"
  | "favorites"
  | "settings"
  | "data"
  | "explorations"
  | "threads"
  | "agent-dev-tools"
  | "code-project"
  | "tasks";

export type HomeTab2 =
  | "home"
  | "collections"
  | "components"
  | "all"
  | "library"
  | "trash"
  | "archive"
  | "settings"
  | "data"
  | "explorations"
  | "threads"
  | "agent-dev-tools"
  | "code-project"
  | "tasks";

export type TabView = "all" | "you" | "sharedWithYou" | "sharedWithOrg";

export type ContextStudioRouteParams = {
  tabView?: ContextStudioRouteParams.TabView;
};
export namespace ContextStudioRouteParams {
  export type TabView =
    | TabView.Overview
    | TabView.Guides
    | TabView.Endorsements
    | TabView.Models
    | TabView.Setup
    | TabView.Suggestions
    | TabView.Evals
    | TabView.Repositories
    | TabView.Apps;

  /** @default "overview" */
  export namespace TabView {
    export const Overview = "overview";
    export type Overview = typeof Overview;
    export const Guides = "guides";
    export type Guides = typeof Guides;
    export const Endorsements = "endorsements";
    export type Endorsements = typeof Endorsements;
    export const Models = "models";
    export type Models = typeof Models;
    export const Setup = "setup";
    export type Setup = typeof Setup;
    export const Suggestions = "suggestions";
    export type Suggestions = typeof Suggestions;
    export const Evals = "evals";
    export type Evals = typeof Evals;
    export const Repositories = "repositories";
    export type Repositories = typeof Repositories;
    export const Apps = "apps";
    export type Apps = typeof Apps;
  }
}

export type ContextStudioModelsRouteParams = {
  semanticProjectId?: SemanticProjectId;
  modelView?: ContextStudioModelsRouteParams.ModelView;
};

export namespace ContextStudioModelsRouteParams {
  export type ModelView = ModelView.References;
  export namespace ModelView {
    export const References = "references";
    export type References = typeof References;
  }
}

export const ContextStudioSuggestionsQueryParams = {
  sortField: "sortField",
  sortDirection: "sortDirection",
  status: "status",
} as const;
export namespace ContextStudioSuggestionsQueryParams {
  export type SortField = "CREATED_DATE" | "EVIDENCE_COUNT";
  export type SortDirection = "ASC" | "DESC";
  export type Status = ContextSuggestionIssueStatus;
}

export type ContextSuggestionsRouteParams = {
  issueId?: ContextSuggestionIssueId;
  issueTabId?: ContextSuggestionsRouteParams.IssueTabId;
};

export namespace ContextSuggestionsRouteParams {
  export type IssueTabId = IssueTabId.Summary | IssueTabId.Changes;
  /** @default "summary" */
  export namespace IssueTabId {
    export const Summary = "summary";
    export type Summary = typeof Summary;
    export const Changes = "changes";
    export type Changes = typeof Changes;
  }
}

export type ContextStudioEvalsRouteParams = {
  suiteRunId?: CustomerEvalSuiteRunId;
};

export type ContextSuggestionChangesHash =
  | ContextSuggestionChangesHash.Section
  | ContextSuggestionChangesHash.UpdateId;

export namespace ContextSuggestionChangesHash {
  export type Section =
    | Section.DataWarehouse
    | Section.Guides
    | Section.Models
    | Section.WorkspaceContext
    | Section.AssetStatuses;
  export namespace Section {
    export const DataWarehouse = "dataWarehouse";
    export type DataWarehouse = typeof DataWarehouse;
    export const Guides = "guides";
    export type Guides = typeof Guides;
    export const Models = "models";
    export type Models = typeof Models;
    export const WorkspaceContext = "workspaceContext";
    export type WorkspaceContext = typeof WorkspaceContext;
    export const AssetStatuses = "assetStatuses";
    export type AssetStatuses = typeof AssetStatuses;
  }

  export type UpdateId = UpdateId.DataWarehouse | UpdateId.File;
  export namespace UpdateId {
    export type DataWarehouse = ContextSuggestionDataWarehouseUpdateId;
    // reflection for trace-ability & type-safety
    export const dataWarehouse = identity<DataWarehouse>;
    export type File = ContextSuggestionFileUpdateId;
    export const file = identity<File>;
  }
}

export namespace ContextWorkbenchQueryParams {
  // -- KEYS --

  /** If specified, opens the specified file path in the workbench. */
  export const filePath = "file";
  export const publishView = "preview";
  export const publishChangesetId = "previewId";
  export const fileAction = "action";

  // -- VALUES --

  export type PublishView = ContextWorkbenchPublish.View;
  export type PublishChangesetId = ContextChangesetId;
  export type FilePath = string | null;
  export type FileAction = FileAction.Create | FileAction.View;
  export namespace FileAction {
    export const Create = "create";
    export type Create = typeof Create;
    export const View = "view";
    export type View = typeof View;
  }
}

export type AccountSettingsTabs =
  | "preferences"
  | "notifications"
  | "connected-apps"
  | "api-keys";

export type WorkspaceSettingsTabs =
  | "general"
  | "compute"
  | "magic"
  | "organization"
  | "styling"
  | "data-sources"
  | "secrets"
  | "integrations"
  | "environment"
  | "scheduled-runs";

export type AccessAndSecuritySettingsTabs =
  | "users"
  | "groups"
  | "directory-sync"
  | "audit-logs"
  | "sso"
  | "project-security"
  | "data-retention";

export type PlanAndBillingSettingsTabs = "billing" | "tiers";

export type AllSettingsTabs =
  | AccountSettingsTabs
  | WorkspaceSettingsTabs
  | AccessAndSecuritySettingsTabs
  | PlanAndBillingSettingsTabs;

export type WorkspacePickerType = "signup" | "signUp" | "login";

export type SemanticProjectWorkbenchTab = "datasets" | "preview";
export type SemanticProjectWorkbenchPublishTab =
  | "changes"
  | "references"
  | "preview"
  | "ask-preview";
export const SemanticProjectWorkbenchQueryParams = {
  publishPreviewVersionId: "previewId" as const,
  publishTab: "publishTab" as const,
  filePath: "file" as const,
  threadId: "threadId" as const,
};

export const DataBrowserTabRouteLiteral = Union(
  Literal("favorites"),
  Literal("recently-used"),
  Literal("all"),
  Literal("search"),
  Literal("semantic-layer"),
  Literal("curation"),
  Literal("recommended"),
);

export type DataBrowserTabRoute = Static<typeof DataBrowserTabRouteLiteral>;
export const DataBrowserTabRoute = getNormalEnum(DataBrowserTabRouteLiteral);

export type DataSourceId =
  | DataConnectionId
  | DataSourceDatabaseId
  | DataSourceSchemaId
  | DataSourceTableId;

/** Keys expected by Routes.HOME, that serialized and are used to set URLSearchParams */
export const HomeRouteParamLiterals = Union(
  Literal("statusNames"),
  Literal("categoryNames"),
  Literal("sort"),
  Literal("duration"),
  Literal("direction"),
  Literal("search"),
  Literal("sharedWithWorkspace"),
  Literal("access"),
  Literal("sharedToWeb"),
  Literal("scheduled"),
  Literal("owners"),
  Literal("creators"),
  Literal("requiresReview"),
  Literal("pendingReview"),
  Literal("collections"),
  Literal("published"),
  Literal("favorited"),
  Literal("archived"),
  Literal("computeProfileImages"),
  Literal("computeProfileSizes"),
);

/** Type to ensure this sentinel value can stay consistently named with the HexFilter.EXCLUDE enum. **/
export const ExcludeOptionParam: Lowercase<typeof HexFilter.EXCLUDE> =
  "exclude";
export type ExcludeOptionParam = typeof ExcludeOptionParam;

export type HomeRouteParamLiterals = Static<typeof HomeRouteParamLiterals>;
export const HomeRouteParamKeys = getNormalEnum(HomeRouteParamLiterals);
export type HomeRouteParamKeys = typeof HomeRouteParamKeys;

export type HomeQueryParams = Readonly<{
  statusNames?: readonly string[] | ExcludeOptionParam;
  categoryNames?: readonly string[] | ExcludeOptionParam;
  access?: readonly string[];
  owners?: readonly string[];
  creators?: readonly string[];
  sort?: string;
  duration?: string;
  direction?: string;
  search?: string;
  sharedWithWorkspace?: string;
  sharedToWeb?: string;
  requiresReview?: string;
  pendingReview?: string;
  scheduled?: string;
  collections?: readonly string[] | ExcludeOptionParam;
  published?: string;
  favorited?: string;
  archived?: string;
  computeProfileImages?: readonly string[];
  computeProfileSizes?: readonly string[];
  promptExample?: string;
}>;

export const HomeQueryParamsKey = {
  STATUS: "status",
  CATEGORY: "category",
  SHARED_WITH_WORKSPACE: "sharedWithWorkspace",
  SCHEDULED: "scheduled",
  ACCESS: "access",
  SHARED_TO_WEB: "sharedToWeb",
  FAVORITED: "favorited",
  SORT: "sort",
  /**
   * Ordering direction for the sort attribute.
   */
  DIRECTION: "direction",
  VIEW_COUNT_DURATION: "duration",
  /** Search term filter params */
  SEARCH: "search",
  OWNER: "owner",
  CREATOR: "creator",
  REQUIRES_REVIEW: "requiresReview",
  PENDING_REVIEW: "pendingReview",
  COLLECTION: "collection",
  PUBLISHED: "published",
  ARCHIVED: "archived",
  COMPUTE_PROFILE_IMAGE: "computeProfileImage",
  COMPUTE_PROFILE_SIZE: "computeProfileSize",
  /** Sentinel key for pre-filling the homepage prompt bar. See `promptExampleSentinels.ts` for the allowed values and security rationale. */
  PROMPT_EXAMPLE: "promptExample",
} as const;

/** Keys of our allowed query parameters, allowed keys used to set URL search params */
export const HomeQueryParamsKeys = Union(
  Literal("status"),
  Literal("category"),
  Literal("access"),
  Literal("owner"),
  Literal("creator"),
  Literal("sort"),
  Literal("direction"),
  Literal("duration"),
  Literal("search"),
  Literal("duration"),
  Literal("sharedWithWorkspace"),
  Literal("sharedToWeb"),
  Literal("requiresReview"),
  Literal("pendingReview"),
  Literal("scheduled"),
  Literal("collection"),
  Literal("published"),
  Literal("favorited"),
  Literal("archived"),
  Literal("computeProfileImage"),
  Literal("computeProfileSize"),
  Literal("promptExample"),
);

export type HomeQueryParamsKeys = Static<typeof HomeQueryParamsKeys>;
export type HomeQueryParamsKey = HomeQueryParamsKeys;

export const useUrlFragment = (): Record<string, string> => {
  const fragment = useLocation().hash.slice(1);
  try {
    return fragment !== "" ? decodeUrlObject(fragment) : {};
  } catch (_) {
    return {};
  }
};

export const useQueryParams = (): URLSearchParams => {
  return new URLSearchParams(useLocation().search);
};

export interface SelectionNavigationState {
  skipScrollDelay?: boolean;
}

export const mergeSelectionNavigationState = (
  state: unknown,
  updates: SelectionNavigationState,
): SelectionNavigationState => ({
  ...((typeof state === "object" && state != null ? state : {}) as Record<
    string,
    unknown
  >),
  ...updates,
});

export const clearSelectionNavigationState = (
  state: unknown,
): Record<string, unknown> | null => {
  if (typeof state !== "object" || state == null) {
    return null;
  }

  const { skipScrollDelay: _skipScrollDelay, ...rest } = state as Record<
    string,
    unknown
  > &
    SelectionNavigationState;

  return Object.keys(rest).length > 0 ? rest : null;
};

export const useQueryParamGetter = (): (() => URLSearchParams) => {
  return useCallback(() => new URLSearchParams(window.location.search), []);
};

export const useDictQueryParams = (): QueryParams => {
  const queryParams = useQueryParams();
  return queryParamsToDict(queryParams);
};

export const useDictQueryParamsGetter = (): (() => Record<string, string>) => {
  return useCallback(() => {
    return queryParamsToDict(new URLSearchParams(location.search));
  }, []);
};

function queryParamsToDict(
  queryParams: URLSearchParams,
): Record<string, string> {
  const newQueryParams: Record<string, string> = {};
  queryParams.forEach((val, key) => {
    newQueryParams[key] = val;
  });
  return newQueryParams;
}

interface WithHash<T extends string = string> {
  hash?: T;
}

export type QueryParams = Record<string, string>;

export interface WithQueryParams {
  queryParams?: QueryParams;
}
export const getAbsoluteCleanUrl = (location: Location): string => {
  return new URL(
    `${ORG_ID}${createPath({
      ...location,
      search: undefined,
      hash: undefined,
    })}`,
    window.location.origin,
  ).href;
};

/* Will only work in user originated callbacks like onClick */
export const copyAbsoluteCleanUrlToClipboard = async (
  location: Location,
): Promise<void> => {
  const url = getAbsoluteCleanUrl(location);
  await navigator.clipboard.writeText(url);
};

// when opening a url in a new page, the url org id needs to be specified
// to prevent redirecting to the login page to determine the org
export function prefixOrgIdToUrl(url: string): string {
  const urlOrgId = getUrlOrg();
  if (urlOrgId == null) {
    return url;
  }
  return url.startsWith("/") ? `/${urlOrgId}${url}` : `${urlOrgId}/${url}`;
}

const getHexUrl = ({
  appSessionId,
  hexLocator,
  prefix = "hex",
  queryParams,
  version,
  view,
}: {
  appSessionId?: AppSessionId;
  hexLocator: HexLocator;
  prefix?: string;
  queryParams?: QueryParams;
  version: string;
  view?: ViewType;
}): string => {
  let maybeAppSessionString = "";
  if (appSessionId) {
    maybeAppSessionString = `/${appSessionId}`;
  }
  let maybeUrlParams = "";
  if (queryParams) {
    maybeUrlParams = encodeQueryParams(queryParams);
  }
  let newUrl = `/${prefix}/${hexLocator}/${version}`;
  switch (view) {
    case "ui":
      newUrl += `/ui`;
      break;
    case "logic":
      newUrl += `/logic`;
      break;
    case "debug":
      newUrl += `/debug`;
      break;
    case "split":
      newUrl += `/split`;
      break;
    case "component":
    case undefined:
      break;
    default:
      assertNever(view, view);
  }

  newUrl += maybeAppSessionString;
  newUrl += maybeUrlParams;

  return newUrl;
};

export interface HexRouteParams {
  /**
   * Typically we would require an intentional choice to omit attribution (via
   * the `NoAttribution` sentinel), but given that it is far and above the
   * exception rather than the rule, we leave it optional and simply patch the
   * specific locations we are concerned about ferrying attribution through.
   */
  attribution?: Attribution;
  hexLocator: HexLocator;
  version: HexVersionString;
  appSessionId?: AppSessionId;
  view?: ViewType;
  queryParams?: { [key: string]: string };
}

export interface ComponentRouteParams {
  hexLocator: HexLocator;
  version: HexVersionString;
  queryParams?: { [key: string]: string };
}

export interface NewProjectRouteParams extends WithQueryParams {
  attribution: Attribution;
  // If specified, short circuits any other process and copies in relevant template.
  templateId?: TemplateId;
  // hexId of base project to duplicate
  baseHexId?: HexId;
  // orgId of base project to duplicate
  baseOrgId?: OrgId;
  // optional id of a tour to start when the project loads
  tour?: TourId;
  // optional id of a dialog to show when the project loads
  onboardingDialog?: OnboardingDialogId;
  // optional context for the onboarding dialog
  onboardingDialogContext?: OnboardingDialogContext;
  // optional name for the newly created project
  projectName?: string;
}

interface WelcomeRouteParams extends WithQueryParams {
  stepIdx?: number;
  orgId?: OrgId;
}

type ExcludedPathParams = "queryParams" | "attribution" | "hash";

export type HexRoutePathParams = Omit<HexRouteParams, ExcludedPathParams>;

// TODO: add eslint rule to prevent things from using `useParams<HexRoutePathParams>()` or other versions
export const useHexRoutePathParams = (): HexRoutePathParams => {
  // TODO: this should only be callable as a sub-route of a hex route that has hex route path params
  return useParams<HexRoutePathParams>() as HexRoutePathParams;
};

export type ComponentRoutePathParams = Omit<
  ComponentRouteParams,
  ExcludedPathParams
>;

export type ExploreRouteParams = {
  hexLocator: HexLocator;
  sessionStateIdOrExploreId?: ExploreSessionStateId | ExploreId;
};

export type ExploreNewRouteParams = {
  hexLocator: HexLocator;
  exploreId: ExploreSessionStateId | ExploreId;
};

/**
 * Util function that will apply all query parameters to the URLSearchParams instance.
 */
export function addHomeQueryParams(
  queryParams: HomeQueryParams,
  searchParamsInstance = new URLSearchParams(),
): URLSearchParams {
  if (queryParams.access != null && queryParams.access.length > 0) {
    searchParamsInstance.set(
      HomeQueryParamsKey.ACCESS,
      queryParams.access.join(",").toLowerCase(),
    );
  }
  if (queryParams.statusNames != null && queryParams.statusNames.length > 0) {
    searchParamsInstance.set(
      HomeQueryParamsKey.STATUS,
      queryParams.statusNames === ExcludeOptionParam
        ? ExcludeOptionParam
        : queryParams.statusNames.join(",").toLowerCase(),
    );
  }
  if (
    queryParams.categoryNames != null &&
    queryParams.categoryNames.length > 0
  ) {
    searchParamsInstance.set(
      HomeQueryParamsKey.CATEGORY,
      queryParams.categoryNames === ExcludeOptionParam
        ? ExcludeOptionParam
        : queryParams.categoryNames.join(",").toLowerCase(),
    );
  }
  if (queryParams.collections != null && queryParams.collections.length > 0) {
    searchParamsInstance.set(
      HomeQueryParamsKey.COLLECTION,
      queryParams.collections === ExcludeOptionParam
        ? ExcludeOptionParam
        : queryParams.collections.join(",").toLowerCase(),
    );
  }
  if (queryParams.sort != null) {
    let sortAndDirection = queryParams.sort.toLowerCase();
    if (queryParams.direction != null) {
      sortAndDirection += `:${queryParams.direction.toLowerCase()}`;
    }
    searchParamsInstance.set(HomeQueryParamsKey.SORT, sortAndDirection);
  }
  if (queryParams.duration != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.VIEW_COUNT_DURATION,
      queryParams.duration,
    );
  }
  if (queryParams.owners != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.OWNER,
      queryParams.owners.join(",").toLowerCase(),
    );
  }
  if (queryParams.creators != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.CREATOR,
      queryParams.creators.join(",").toLowerCase(),
    );
  }
  if (queryParams.search != null) {
    searchParamsInstance.set(HomeQueryParamsKey.SEARCH, queryParams.search);
  }
  if (queryParams.sharedWithWorkspace != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.SHARED_WITH_WORKSPACE,
      queryParams.sharedWithWorkspace,
    );
  }
  if (queryParams.sharedToWeb != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.SHARED_TO_WEB,
      queryParams.sharedToWeb,
    );
  }
  if (queryParams.requiresReview != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.REQUIRES_REVIEW,
      queryParams.requiresReview,
    );
  }
  if (queryParams.pendingReview != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.PENDING_REVIEW,
      queryParams.pendingReview,
    );
  }
  if (queryParams.scheduled != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.SCHEDULED,
      queryParams.scheduled,
    );
  }
  if (queryParams.published != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.PUBLISHED,
      queryParams.published,
    );
  }
  if (queryParams.favorited != null) {
    searchParamsInstance.set(
      HomeQueryParamsKey.FAVORITED,
      queryParams.favorited,
    );
  }
  if (queryParams.archived != null) {
    searchParamsInstance.set(HomeQueryParamsKey.ARCHIVED, queryParams.archived);
  }
  if (
    queryParams.computeProfileImages != null &&
    queryParams.computeProfileImages.length > 0
  ) {
    searchParamsInstance.set(
      HomeQueryParamsKey.COMPUTE_PROFILE_IMAGE,
      queryParams.computeProfileImages.join(","),
    );
  }
  if (
    queryParams.computeProfileSizes != null &&
    queryParams.computeProfileSizes.length > 0
  ) {
    searchParamsInstance.set(
      HomeQueryParamsKey.COMPUTE_PROFILE_SIZE,
      queryParams.computeProfileSizes.join(","),
    );
  }
  // Load-bearing for `Routes.HOME.getUrl({ queryParams: { promptExample } })`
  // — the LD "Try it now" CTA constructs its URL this way.
  if (queryParams.promptExample != null && queryParams.promptExample !== "") {
    searchParamsInstance.set(
      HomeQueryParamsKey.PROMPT_EXAMPLE,
      queryParams.promptExample,
    );
  }
  return searchParamsInstance;
}

export interface DataBrowserRouteParams {
  tab?: DataBrowserTabRoute;
  detailViewType?:
    | DataBrowserIdType
    | DataBrowserSemanticIdType
    | DataBrowserRecommendedIdType;
  dataSourceId?: DataSourceId | SemanticProjectId | SemanticDatasetId;
  semanticResourceName?: SemanticDatasetName | SemanticViewName;
}

export interface AgentDevToolsRouteParams {
  tab?: "threads" | "eval" | "user-profile";
  evalSetId?: string;
  evalScope?: "overview" | "runs" | "compare";
  runId?: string;
  runId2?: string;
}

export type ParamsType = Record<string, any>;

type ParamExtractor<
  Params extends ParamsType,
  ParamName extends string,
  RemainingPath extends string,
  OriginalPath extends string,
  ConsumedParams extends keyof Params = never,
> = ParamName extends `${infer OptionalParamName}?`
  ? OptionalParamName extends OptionalKeysOf<Params>
    ? ValidatePathParamsRecursive<
        RemainingPath,
        OriginalPath,
        Params,
        ConsumedParams | OptionalParamName
      >
    : `Extra optional param: ${OptionalParamName}`
  : ParamName extends RequiredKeysOf<Params>
    ? ValidatePathParamsRecursive<
        RemainingPath,
        OriginalPath,
        Params,
        ConsumedParams | ParamName
      >
    : `Extra required param: ${ParamName}`;

type ValidatePathParamsRecursive<
  Path extends string,
  OriginalPath extends string,
  Params extends ParamsType,
  ConsumedParams extends keyof Params = never,
> = Path extends `${string}:${infer ParamName}/${infer RemainingPath}`
  ? ParamExtractor<
      Params,
      ParamName,
      RemainingPath,
      OriginalPath,
      ConsumedParams
    >
  : Path extends `${string}:${infer ParamName}`
    ? ParamExtractor<Params, ParamName, "", OriginalPath, ConsumedParams>
    : Path extends ""
      ? Omit<Params, ConsumedParams> extends Record<string, never>
        ? OriginalPath
        : `Missing params: ${keyof Omit<Params, ConsumedParams> & string}`
      : OriginalPath;

type ValidatePathParams<
  Path extends string,
  Params extends ParamsType,
> = ValidatePathParamsRecursive<Path, Path, Omit<Params, ExcludedPathParams>>;

type BaseClientSideRoute<Params extends ParamsType> = {
  clientSide: true;
  path: string;
  getUrl: (args: Params) => string;
};

interface ValidatedClientSideRoute<
  Path extends string,
  Params extends ParamsType,
> extends BaseClientSideRoute<Params> {
  path: ValidatePathParams<Path, Params>;
}

export type ClientSideRoute<
  Path extends string,
  Params extends ParamsType,
> = ValidatedClientSideRoute<Path, Params> & {
  href: (orgId: OrgId, withOrigin: boolean, routeArgs: Params) => string;
  push: (navigate: NavigateFunction, routeArgs: Params) => void;
  replace: (navigate: NavigateFunction, routeArgs: Params) => void;
};

export type ClientSideRouteForCurrentHex<
  Params extends { hexLocator: HexLocator },
> = BaseClientSideRoute<Omit<Params, "hexLocator">> & {
  href: (withOrigin: boolean, routeArgs: Omit<Params, "hexLocator">) => string;
  push: (
    navigate: NavigateFunction,
    routeArgs: Omit<Params, "hexLocator">,
  ) => void;
  replace: (
    navigate: NavigateFunction,
    routeArgs: Omit<Params, "hexLocator">,
  ) => void;
};

interface CreateServerSideRoute<Params> {
  clientSide: false;
  getUrl: (args: Params) => string;
}

type ServerSideRoute<Params> = CreateServerSideRoute<Params> & {
  href: (orgId: OrgId, withOrigin: boolean, routeArgs: Params) => string;
};

function href(orgId: OrgId, withOrigin: boolean, url: string): string {
  return `${withOrigin ? window.location.origin : ""}/${orgId}${url}`;
}

export function deepPath(path: string) {
  return path + "/*";
}

export function createClientSideRoute<
  Path extends string,
  Params extends ParamsType,
>(
  route: ValidatedClientSideRoute<Path, Params>,
): ClientSideRoute<Path, Params> {
  return {
    ...route,
    href: (orgId, withOrigin, routeArgs) => {
      return href(orgId, withOrigin, route.getUrl(routeArgs));
    },
    push: (navigate, routeArgs) => {
      return navigate(route.getUrl(routeArgs));
    },
    replace: (navigate, routeArgs) => {
      return navigate(route.getUrl(routeArgs), { replace: true });
    },
  };
}

const createServerSideRoute = <Params extends ParamsType>(
  route: CreateServerSideRoute<Params>,
): ServerSideRoute<Params> => {
  return {
    ...route,
    href: (orgId: OrgId, withOrigin: boolean, routeArgs: Params) => {
      return href(orgId, withOrigin, route.getUrl(routeArgs));
    },
  };
};

export interface SemanticProjectWorkbenchRouteParams {
  semanticProjectId: SemanticProjectId;
  semanticProjectVersionId: SemanticProjectVersionId | SpecialVersionType;
  tab: SemanticProjectWorkbenchTab;
}

export const Routes = {
  SETTINGS: createClientSideRoute({
    clientSide: true,
    path: "/settings/:subView?",
    getUrl: ({ subView }: { subView?: AllSettingsTabs }): string =>
      subView ? `/settings/${subView}` : "/settings",
  }),
  CONTEXT_STUDIO: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/:tabView?",
    getUrl: ({
      queryParams,
      tabView,
    }: ContextStudioRouteParams & WithQueryParams): string =>
      tabView
        ? `/context-studio/${tabView}${encodeQueryParams(queryParams)}`
        : `/context-studio${encodeQueryParams(queryParams)}`,
  }),
  CONTEXT_STUDIO_MODELS: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/models/:semanticProjectId?/:modelView?",
    getUrl: ({
      modelView,
      queryParams,
      semanticProjectId,
    }: ContextStudioModelsRouteParams & WithQueryParams): string =>
      semanticProjectId
        ? modelView
          ? `/context-studio/models/${semanticProjectId}/${modelView}${encodeQueryParams(queryParams)}`
          : `/context-studio/models/${semanticProjectId}${encodeQueryParams(queryParams)}`
        : `/context-studio/models${encodeQueryParams(queryParams)}`,
  }),
  CONTEXT_STUDIO_SUGGESTIONS: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/suggestions/:issueId?/:issueTabId?",
    getUrl: ({
      hash,
      issueId,
      issueTabId,
      queryParams,
    }: ContextSuggestionsRouteParams &
      WithQueryParams &
      WithHash<ContextSuggestionChangesHash>): string =>
      issueId
        ? issueTabId
          ? `/context-studio/suggestions/${issueId}/${issueTabId}${encodeQueryParams(queryParams)}${encodeHash(hash)}`
          : `/context-studio/suggestions/${issueId}${encodeQueryParams(queryParams)}${encodeHash(hash)}`
        : `/context-studio/suggestions${encodeQueryParams(queryParams)}${encodeHash(hash)}`,
  }),
  CONTEXT_STUDIO_EVALS: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/evals/:suiteRunId?",
    getUrl: ({
      queryParams,
      suiteRunId,
    }: ContextStudioEvalsRouteParams & WithQueryParams): string =>
      suiteRunId
        ? `/context-studio/evals/${suiteRunId}${encodeQueryParams(queryParams)}`
        : `/context-studio/evals${encodeQueryParams(queryParams)}`,
  }),
  CONTEXT_STUDIO_EVALS_COMPARE: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/evals/compare",
    getUrl: ({ queryParams }: WithQueryParams): string =>
      `/context-studio/evals/compare${encodeQueryParams(queryParams)}`,
  }),
  AGENT_DEV_TOOLS: createClientSideRoute({
    clientSide: true,
    path: "/home/agent-dev-tools/:tab?/:evalSetId?/:evalScope?/:runId?/:runId2?",
    getUrl: ({
      evalScope,
      evalSetId,
      runId,
      runId2,
      tab = "threads",
    }: AgentDevToolsRouteParams): string => {
      if (tab === "eval" && evalSetId) {
        const normalizedScope = evalScope;

        if (!normalizedScope || normalizedScope === "overview") {
          return `/home/agent-dev-tools/eval/${evalSetId}`;
        }

        if (normalizedScope === "runs") {
          if (runId) {
            return `/home/agent-dev-tools/eval/${evalSetId}/runs/${runId}`;
          }
          return `/home/agent-dev-tools/eval/${evalSetId}/runs`;
        }

        if (normalizedScope === "compare") {
          if (runId && runId2) {
            return `/home/agent-dev-tools/eval/${evalSetId}/${normalizedScope}/${runId}/${runId2}`;
          }
          return `/home/agent-dev-tools/eval/${evalSetId}/${normalizedScope}`;
        }

        return `/home/agent-dev-tools/eval/${evalSetId}`;
      }
      if (tab === "eval") {
        return `/home/agent-dev-tools/eval`;
      }
      return `/home/agent-dev-tools/${tab}`;
    },
  }),
  DATA_BROWSER: createClientSideRoute({
    clientSide: true,
    path: "/home/data/:tab?/:detailViewType?/:dataSourceId?/:semanticResourceName?",
    getUrl: ({
      dataSourceId,
      detailViewType,
      queryParams,
      semanticResourceName,
      tab,
    }: DataBrowserRouteParams & { queryParams?: HomeQueryParams }): string => {
      let queryParamStr = "";
      if (queryParams != null) {
        queryParamStr = "?" + addHomeQueryParams(queryParams).toString();
      }

      if (!tab) {
        return `/home/data${queryParamStr}`;
      }

      if (
        tab === DataBrowserTabRoute.recommended &&
        DataBrowserRecommendedIdTypeLiteral.guard(detailViewType)
      ) {
        return `/home/data/${tab}/${detailViewType}${queryParamStr}`;
      }

      const hasDetailViewType =
        tab === DataBrowserTabRoute.all ||
        tab === DataBrowserTabRoute["semantic-layer"];

      if (hasDetailViewType && detailViewType != null && dataSourceId != null) {
        let suffix = "";
        if (
          tab === DataBrowserTabRoute["semantic-layer"] &&
          (detailViewType === DataBrowserSemanticIdType.DATASET ||
            detailViewType === DataBrowserSemanticIdType.VIEW) &&
          semanticResourceName != null
        ) {
          suffix = `/${semanticResourceName}`;
        }
        return `/home/data/${tab}/${detailViewType}/${dataSourceId}${suffix}${queryParamStr}`;
      }

      return `/home/data/${tab}${queryParamStr}`;
    },
  }),
  CHECKOUT: createClientSideRoute({
    clientSide: true,
    path: "/checkout",
    getUrl: ({ queryParams }: WithQueryParams = {}): string =>
      `/checkout${encodeQueryParams(queryParams)}`,
  }),
  // This includes a route for platform admins who may not be org admins
  ADMIN_PLATFORM: createClientSideRoute({
    clientSide: true,
    path: "/admin-platform/:orgId?",
    getUrl: ({ orgId }: { orgId?: OrgId }): string =>
      `/admin-platform${orgId != null ? `/${orgId}` : ""}`,
  }),
  SCREENSHOT: createClientSideRoute({
    clientSide: true,
    path: "/screenshot/:hexLocator/:version/:appSessionId",
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
      version,
    }: {
      hexLocator: HexLocator;
      version: HexVersionString;
      appSessionId: AppSessionId;
    } & WithQueryParams): string =>
      `/screenshot/${hexLocator}/${version}/${appSessionId}${encodeQueryParams(queryParams)}`,
  }),
  PROJECT_SOURCE_SCREENSHOT: createClientSideRoute({
    clientSide: true,
    path: "/project-source/:projectId/branches/:branchId/screenshot",
    getUrl: ({
      branchId,
      projectId,
      queryParams,
    }: {
      branchId: import("@hex/common").EditSessionBranchId;
      projectId: PlainCodeProjectId;
    } & WithQueryParams): string =>
      `/project-source/${projectId}/branches/${branchId}/screenshot${encodeQueryParams(queryParams)}`,
  }),
  APP: createClientSideRoute({
    clientSide: true,
    path: "/app/:hexLocator/:version/:appSessionId?",
    getUrl: ({
      appSessionId,
      attribution = NoAttribution,
      hexLocator,
      queryParams,
      version,
      // Omit view as it is irrelevant to app route
    }: Omit<HexRouteParams, "view">): string =>
      withAttribution(
        getHexUrl({
          appSessionId,
          hexLocator: hexLocator,
          version,
          prefix: "app",
          queryParams,
        }),
        attribution,
      ),
  }),
  APP_BASE: createClientSideRoute({
    clientSide: true,
    path: "/app/:hexLocator",
    getUrl: ({
      hexLocator,
      queryParams,
    }: { hexLocator: HexLocator } & WithQueryParams): string =>
      `/app/${hexLocator}${encodeQueryParams(queryParams)}`,
  }),
  APP_LATEST: createClientSideRoute({
    clientSide: true,
    path: `/app/:hexLocator/${SpecialVersionType.LAST_PUBLISHED}/:appSessionId?`,
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
    }: {
      hexLocator: HexLocator;
      appSessionId?: AppSessionId;
    } & WithQueryParams): string => {
      let url = `/app/${hexLocator}/${SpecialVersionType.LAST_PUBLISHED}`;
      if (appSessionId != null) {
        url = `${url}/${appSessionId}`;
      }
      if (queryParams != null) {
        url = `${url}${encodeQueryParams(queryParams)}`;
      }
      return url;
    },
  }),
  MAGIC_AUTH_CALLBACK: createClientSideRoute({
    clientSide: true,
    path: "/auth/magic/callback",
    getUrl: ({ queryParams }: WithQueryParams): string =>
      `/auth/magic/callback${encodeQueryParams(queryParams)}`,
  }),
  AZURE_AUTH: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      orgId,
      queryParams,
    }: { orgId?: OrgId } & WithQueryParams): string =>
      orgId != null
        ? `/auth/${orgId}/azure${encodeQueryParams(queryParams)}`
        : `/auth-all/azure${encodeQueryParams(queryParams)}`,
  }),
  AZURE_SIGNUP: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      attribution,
      queryParams,
    }: { attribution: Attribution } & WithQueryParams): string =>
      withAttribution(
        `/auth/azure/signup${encodeQueryParams(queryParams)}`,
        attribution,
      ),
  }),
  GOOGLE_AUTH: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      orgId,
      queryParams,
    }: { orgId?: OrgId } & WithQueryParams): string =>
      orgId != null
        ? `/auth/${orgId}/google${encodeQueryParams(queryParams)}`
        : `/auth-all/google${encodeQueryParams(queryParams)}`,
  }),
  GOOGLE_SIGNUP: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      attribution,
      queryParams,
    }: { attribution: Attribution } & WithQueryParams): string =>
      withAttribution(
        `/auth/google/signup${encodeQueryParams(queryParams)}`,
        attribution,
      ),
  }),
  SSO_AUTH: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      orgId,
      queryParams,
    }: { orgId: OrgId } & WithQueryParams): string =>
      `/auth/${orgId}/sso${encodeQueryParams(queryParams)}`,
  }),
  CREATE_USER: createClientSideRoute({
    clientSide: true,
    path: "/createUser",
    getUrl: (): string => "/createUser",
  }),
  LOGIC: createClientSideRoute({
    clientSide: true,
    path: "/hex/:hexLocator/:version/logic/:appSessionId?",
    getUrl: ({
      appSessionId,
      attribution = NoAttribution,
      hexLocator,
      queryParams,
      version,
      // Omit view as we hardcode it to `logic`
    }: Omit<HexRouteParams, "view">): string =>
      withAttribution(
        getHexUrl({
          hexLocator: hexLocator,
          view: "logic",
          version,
          appSessionId,
          queryParams,
        }),
        attribution,
      ),
  }),
  DEBUG: createClientSideRoute({
    clientSide: true,
    path: "/hex/:hexLocator/:version/debug/:appSessionId?",
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
      version,
      // Omit view as we hardcode it to `debug`
    }: Omit<HexRouteParams, "view">): string =>
      getHexUrl({
        hexLocator: hexLocator,
        view: "debug",
        version,
        appSessionId,
        queryParams: {
          view: serializeProjectViews([
            EditViewType.NOTEBOOK,
            EditViewType.APP,
          ]),
          ...queryParams,
        },
      }),
  }),
  COMPONENT: createClientSideRoute({
    clientSide: true,
    path: "/component/:hexLocator/:version",
    getUrl: ({
      hexLocator,
      queryParams,
      version,
    }: ComponentRouteParams): string =>
      getHexUrl({
        hexLocator: hexLocator,
        version,
        prefix: "component",
        queryParams,
      }),
  }),
  NEW_COLLECTION: createClientSideRoute({
    clientSide: true,
    path: "/home/collections?dialog=new-collection",
    getUrl: (): string => {
      return `/home/collections?dialog=new-collection`;
    },
  }),
  COLLECTION: createClientSideRoute({
    clientSide: true,
    path: "/collection/:collectionId/:collectionName?",
    getUrl: ({
      collectionId,
      collectionName,
      queryParams,
    }: {
      collectionId: CollectionId;
      queryParams?: HomeQueryParams;
      collectionName?: string;
    }): string => {
      let url = `/collection/${collectionId}/${Slugger.slug(collectionName)}`;
      if (queryParams != null) {
        const searchParamsInstance = addHomeQueryParams(queryParams);
        url = `${url}?${searchParamsInstance.toString()}`;
      }
      return url;
    },
  }),
  SPLIT_SCREEN: createClientSideRoute({
    clientSide: true,
    path: "/hex/:hexLocator/:version/split/:appSessionId?",
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
      version,
      // Omit view as we hardcode it to `split`
    }: Omit<HexRouteParams, "view">): string =>
      getHexUrl({
        hexLocator: hexLocator,
        view: "split",
        version,
        appSessionId,
        queryParams,
      }),
  }),
  // this route isn't used for anything but redirection
  HEX_BASE: createClientSideRoute({
    clientSide: true,
    path: "/hex/:hexLocator/:version?/:view?/:appSessionId?",
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
      version,
      view,
    }: {
      hexLocator: HexLocator;
      queryParams?: QueryParams;
      version?: HexVersionString;
      view?: ViewType;
      appSessionId?: AppSessionId;
    }): string => {
      if (version == null) {
        return `/hex/${hexLocator}${encodeQueryParams(queryParams)}`;
      }
      return getHexUrl({
        appSessionId,
        hexLocator,
        queryParams,
        version,
        view,
      });
    },
  }),
  // this route isn't used for anything but redirection
  COMPONENT_BASE: createClientSideRoute({
    clientSide: true,
    path: "/component/:hexLocator/:version?/:view?/:appSessionId?",
    getUrl: ({
      hexLocator,
    }: {
      hexLocator: HexLocator;
      // These are required for type safety, but are not used in the route
      version?: HexVersionString;
      view?: ViewType;
      appSessionId?: AppSessionId;
    }): string => `/hex/${hexLocator}`,
  }),
  NEW_PROJECT: createClientSideRoute({
    clientSide: true,
    path: "/new-project",
    getUrl: ({
      attribution,
      baseHexId,
      baseOrgId,
      onboardingDialog,
      onboardingDialogContext,
      projectName,
      queryParams,
      templateId,
      tour,
    }: NewProjectRouteParams) =>
      withAttribution(
        `/new-project${encodeQueryParams({
          ...queryParams,
          ...(baseHexId != null && { baseHexId }),
          ...(baseOrgId != null && { baseOrgId }),
          ...(templateId != null && { demoId: templateId.toLowerCase() }),
          ...(tour != null && { tour }),
          ...(onboardingDialog != null && { onboardingDialog }),
          ...(onboardingDialogContext != null && { onboardingDialogContext }),
          ...(projectName != null && { projectName }),
        })}`,
        attribution,
      ),
  }),
  NEW_THREAD: createClientSideRoute({
    clientSide: true,
    path: "/new-thread",
    getUrl: ({ attribution = NoAttribution }: { attribution?: Attribution }) =>
      withAttribution("/new-thread", attribution),
  }),
  HOME: createClientSideRoute({
    clientSide: true,
    path: "/home/:subView?/:tabView?",
    getUrl: ({
      queryParams,
      subView,
      tabView,
    }: {
      queryParams?: HomeQueryParams;
      subView?: HomeTab;
      tabView?: TabView;
    }): string => {
      let url =
        subView != null && subView !== "home" ? `/home/${subView}` : "/home";

      if (
        tabView != null &&
        (subView === "components" ||
          subView === "collections" ||
          subView === "code-project")
      ) {
        url = `${url}/${tabView}`;
      }

      if (queryParams != null) {
        const searchParamsInstance = addHomeQueryParams(queryParams);
        url = `${url}?${searchParamsInstance.toString()}`;
      }

      return url;
    },
  }),
  PROJECT_SOURCE: createClientSideRoute({
    clientSide: true,
    path: "/project-source/:projectId",
    getUrl: ({
      projectId,
      queryParams,
    }: {
      projectId: PlainCodeProjectId;
      queryParams?: { threadId?: string };
    }): string => {
      const base = `/project-source/${projectId}`;
      if (queryParams?.threadId != null) {
        return `${base}?${new URLSearchParams({ threadId: queryParams.threadId }).toString()}`;
      }
      return base;
    },
  }),
  PROJECT_SOURCE_BRANCH: createClientSideRoute({
    clientSide: true,
    path: "/project-source/:projectId/branches/:branchId",
    getUrl: ({
      branchId,
      projectId,
      queryParams,
    }: {
      branchId: import("@hex/common").EditSessionBranchId;
      projectId: PlainCodeProjectId;
      queryParams?: { threadId?: string };
    }): string => {
      const base = `/project-source/${projectId}/branches/${branchId}`;
      if (queryParams?.threadId != null) {
        return `${base}?${new URLSearchParams({ threadId: queryParams.threadId }).toString()}`;
      }
      return base;
    },
  }),
  LOGIN: createClientSideRoute({
    clientSide: true,
    path: "/login",
    getUrl: ({
      attribution,
      orgId,
      queryParams,
    }: { attribution: Attribution; orgId?: OrgId } & WithQueryParams): string =>
      withAttribution(
        orgId != null
          ? `/${orgId}/login${encodeQueryParams(queryParams)}`
          : `/login${encodeQueryParams(queryParams)}`,
        attribution,
      ),
  }),
  LOGIN_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/login-success",
    getUrl: (): string => `/login-success`,
  }),
  GITHUB_PACKAGE_CONNECT_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/github-connect-success",
    getUrl: (): string => "/github-connect-success",
  }),
  GITHUB_SYNC_CONNECT_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/githubsync-connect-success",
    getUrl: (): string => "/githubsync-connect-success",
  }),
  GITHUB_SERVER_PACKAGE_CONNECT_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/github-server/package-import/connect-success",
    getUrl: (): string =>
      "/internal-api/v1/git/github-server/package-import/connect-success",
  }),
  GITHUB_SERVER_SYNC_CONNECT_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/github-server/export/connect-success",
    getUrl: (): string =>
      "/internal-api/v1/git/github-server/export/connect-success",
  }),
  GITHUB_PACKAGE_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/github-install-landing",
    getUrl: (): string => "/github-install-landing",
  }),
  GITHUB_SERVER_PACKAGE_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/github-server/package-import/install-landing",
    getUrl: (): string =>
      "/internal-api/v1/git/github-server/package-import/install-landing",
  }),
  GITHUB_SERVER_SYNC_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/github-server/export/install-landing",
    getUrl: (): string =>
      "/internal-api/v1/git/github-server/export/install-landing",
  }),
  GITHUB_SYNC_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/githubsync-install-landing",
    getUrl: (): string => "/githubsync-install-landing",
  }),
  GITLAB_SYNC_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/gitlabsync-install-landing",
    getUrl: (): string => "/gitlabsync-install-landing",
  }),
  GITLAB_PACKAGE_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/gitlab/package-import/install-landing",
    getUrl: (): string =>
      "/internal-api/v1/git/gitlab/package-import/install-landing",
  }),
  GITLAB_SERVER_PACKAGE_INSTALL_LANDING: createClientSideRoute({
    clientSide: true,
    path: "/internal-api/v1/git/gitlab-server/package-import/install-landing",
    getUrl: (): string =>
      "/internal-api/v1/git/gitlab-server/package-import/install-landing",
  }),
  MCP_SERVER_OAUTH_RESULT: createClientSideRoute({
    clientSide: true,
    path: "/mcp-server-oauth-result",
    getUrl: (): string => "/mcp-server-oauth-result",
  }),
  NOTION_UNFURL_AUTHORIZE: createClientSideRoute({
    clientSide: true,
    path: "/notion-unfurl/authorize",
    getUrl: (): string => "/notion-unfurl/authorize",
  }),
  SLACK_INSTALL: createServerSideRoute({
    clientSide: false,
    getUrl: ({ orgId }: { orgId: OrgId }): string =>
      `/slack-auth/install/${orgId}`,
  }),
  SLACK_ORG_INSTALL: createServerSideRoute({
    clientSide: false,
    getUrl: ({
      enableMagicSlackbot,
      orgId,
    }: {
      orgId: OrgId;
      enableMagicSlackbot?: boolean;
    }): string => {
      const queryParams = new URLSearchParams();
      if (enableMagicSlackbot) {
        queryParams.set("enableMagicSlackbot", "true");
      }
      return `/slack-auth/org-install/${orgId}?${queryParams.toString()}`;
    },
  }),
  BIGQUERY_OAUTH_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/bigquery-oauth-success",
    getUrl: (): string => "/bigquery-oauth-success",
  }),
  SNOWFLAKE_OAUTH_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/snowflake-oauth-success",
    getUrl: ({ queryParams }: WithQueryParams = {}): string =>
      `/snowflake-oauth-success${encodeQueryParams(queryParams)}`,
  }),
  DATABRICKS_OAUTH_SUCCESS: createClientSideRoute({
    clientSide: true,
    path: "/databricks-oauth-success",
    getUrl: (): string => "/databricks-oauth-success",
  }),
  CLI_AUTHORIZE: createClientSideRoute({
    clientSide: true,
    path: "/cli/authorize",
    getUrl: ({ queryParams }: WithQueryParams = {}): string =>
      `/cli/authorize${encodeQueryParams(queryParams)}`,
  }),
  LOGOUT: createServerSideRoute({
    clientSide: false,
    getUrl: ({ orgId }: { orgId?: OrgId }): string =>
      orgId != null ? `/logout/${orgId}` : `/logout-all`,
  }),
  LINK_INVALID: createClientSideRoute({
    clientSide: true,
    path: "/link",
    getUrl: (): string => "/link",
  }),
  LINK_WRONG_USER: createClientSideRoute({
    clientSide: true,
    path: "/link/:key/wrong-user",
    getUrl: ({ key }: { key: string }): string => `/link/${key}/wrong-user`,
  }),
  LINK_LOGGED_IN: createClientSideRoute({
    clientSide: true,
    path: "/link/:key/logged-in",
    getUrl: ({ key }: { key: string }): string => `/link/${key}/logged-in`,
  }),
  EMBED_APP_TEST_PAGE: createClientSideRoute({
    clientSide: true,
    // Note that this is a non Hex route (i.e. unauthenticated and does not start with `/:orgId`)
    // This url can also be changed as we hard link it from `embeddingController.ts`
    path: "/app/embedded-test-page",
    getUrl: (): string => "/app/embedded-test-page",
  }),
  MCP_APP_TEST_HARNESS: createClientSideRoute({
    clientSide: true,
    // Test harness that loads MCP bundle in iframe with proper MCP protocol communication
    // Used for E2E testing of HexAppRenderer
    path: "/app/mcp-test-harness",
    getUrl: (): string => "/app/mcp-test-harness",
  }),
  EMAIL_REDIRECTED: createClientSideRoute({
    clientSide: true,
    // Note that this is a non Hex route (i.e. unauthenticated and does not start with `/:orgId`)
    path: "/app/redirected",
    getUrl: (): string => "/app/redirected",
  }),
  SIGNUP: createClientSideRoute({
    clientSide: true,
    path: "/signup/:promo?",
    getUrl: ({
      attribution,
      promo,
      queryParams,
    }: {
      attribution: Attribution;
      promo?: PromoCode;
    } & WithQueryParams): string => {
      let url = "/signup";
      if (promo) {
        url = `${url}/${promo}`;
      }
      return withAttribution(
        `${url}${encodeQueryParams(queryParams)}`,
        attribution,
      );
    },
  }),
  SPELLBOOK: createClientSideRoute({
    clientSide: true,
    path: "/spellbook/:subView?",
    getUrl: ({ subView }: { subView?: string }): string =>
      subView ? `/spellbook/${subView}` : "/spellbook",
  }),
  MAGIC_LINK_SENT: createClientSideRoute({
    clientSide: true,
    path: "/sent",
    getUrl: ({
      attribution,
      code,
      email,
      queryParams,
    }: {
      attribution: Attribution;
      code: string;
      email: string;
    } & WithQueryParams): string =>
      withAttribution(
        `/sent${encodeQueryParams({
          ...queryParams,
          code,
        })}#${encodeUrlObject({ email })}`,
        attribution,
      ),
  }),
  WORKSPACE_PICKER: createClientSideRoute({
    clientSide: true,
    path: "/workspace-picker/:subView",
    getUrl: ({
      attribution,
      queryParams,
      subView,
    }: {
      attribution: Attribution;
      subView: WorkspacePickerType;
    } & WithQueryParams): string =>
      withAttribution(
        `/workspace-picker/${subView}${encodeQueryParams(queryParams)}`,
        attribution,
      ),
  }),
  WELCOME: createClientSideRoute({
    clientSide: true,
    path: "/welcome",
    getUrl: ({ orgId, queryParams, stepIdx }: WelcomeRouteParams): string => {
      const baseUrl = orgId ? `/${orgId}/welcome` : "/welcome";

      if (stepIdx != null) {
        return `${baseUrl}/step/${stepIdx}${encodeQueryParams(queryParams)}`;
      } else {
        return `${baseUrl}${encodeQueryParams(queryParams)}`;
      }
    },
  }),
  ONBOARDING: createClientSideRoute({
    clientSide: true,
    path: "/onboarding",
    getUrl: ({ queryParams }: WithQueryParams): string =>
      `/onboarding${encodeQueryParams(queryParams)}`,
  }),
  PROMPT_ONBOARDING_EXPERIENCE: createClientSideRoute({
    clientSide: true,
    path: "/onboarding/prompt",
    getUrl: ({ queryParams }: WithQueryParams): string =>
      `/onboarding/prompt${encodeQueryParams(queryParams)}`,
  }),
  ORG: createClientSideRoute({
    clientSide: true,
    path: "/:orgId",
    getUrl: ({ orgId }: { orgId: OrgId }): string => `/${orgId}`,
  }),
  ORG_SELECTOR: createClientSideRoute({
    clientSide: true,
    path: "/org-selector",
    getUrl: ({ queryParams }: WithQueryParams = {}): string =>
      `/org-selector${encodeQueryParams(queryParams)}`,
  }),
  SUPPORT_EMAIL: createServerSideRoute({
    clientSide: false,
    getUrl: (): string => "mailto:support@hex.tech",
  }),
  STATUS: createServerSideRoute({
    clientSide: false,
    getUrl: (): string => "https://status.hex.tech/",
  }),
  UI: createClientSideRoute({
    clientSide: true,
    path: "/hex/:hexLocator/:version/ui/:appSessionId?",
    getUrl: ({
      appSessionId,
      hexLocator,
      queryParams,
      version,
      // Omit view as we hardcode it to `ui`
    }: Omit<HexRouteParams, "view">): string =>
      getHexUrl({
        hexLocator: hexLocator,
        view: "ui",
        appSessionId,
        version,
        queryParams,
      }),
  }),
  APP_ICON: createClientSideRoute({
    clientSide: true,
    path: "/app-icon/:orgId",
    getUrl: ({ orgId }: { orgId: OrgId }): string => `/app-icon/${orgId}`,
  }),
  FAVICON: createClientSideRoute({
    clientSide: true,
    path: "/favicon/:orgId",
    getUrl: ({ orgId }: { orgId: OrgId }): string => `/favicon/${orgId}`,
  }),
  GOOGLE_DRIVE_OAUTH_COMPLETE: createClientSideRoute({
    clientSide: true,
    path: "/google-drive-oauth-complete",
    getUrl: (): string => "/google-drive-oauth-complete",
  }),
  EXPLORE: createClientSideRoute({
    clientSide: true,
    path: "/explore/:hexLocator/:sessionStateIdOrExploreId?",
    getUrl: ({
      hexLocator,
      sessionStateIdOrExploreId,
    }: ExploreRouteParams): string =>
      sessionStateIdOrExploreId != null
        ? `/explore/${hexLocator}/${sessionStateIdOrExploreId}`
        : `/explore/${hexLocator}`,
  }),
  EXPLORE_NEW: createClientSideRoute({
    clientSide: true,
    path: "/explore-new/:hexLocator/:exploreId",
    getUrl: ({ exploreId, hexLocator }: ExploreNewRouteParams): string =>
      `/explore-new/${hexLocator}/${exploreId}`,
  }),
  EXPLORE_ERROR: createClientSideRoute({
    clientSide: true,
    path: "/explore-error",
    getUrl: (): string => "/explore-error",
  }),
  // If the threadId is provided: the thread view
  // If the threadId is not provided: the AskFromHomeTab for a new thread.
  THREAD: createClientSideRoute({
    clientSide: true,
    path: "/thread/:threadId?",
    getUrl: ({
      queryParams,
      threadId,
    }: {
      threadId?: AgentChatThreadId | ThreadLocator;
    } & WithQueryParams): string =>
      `/thread${threadId ? `/${threadId}` : ""}${encodeQueryParams(queryParams)}`,
  }),
  // the workbench for viewing/editing a semantic project at the specified version
  SEMANTIC_PROJECT_WORKBENCH: createClientSideRoute({
    clientSide: true,
    path: "/model-workbench/:semanticProjectId/:semanticProjectVersionId/:tab",
    getUrl: ({
      queryParams,
      semanticProjectId,
      semanticProjectVersionId = SpecialVersionType.DRAFT,
      tab,
    }: WithQueryParams & SemanticProjectWorkbenchRouteParams): string => {
      return `/model-workbench/${semanticProjectId}/${semanticProjectVersionId}/${tab}${encodeQueryParams(queryParams)}`;
    },
  }),
  // only used for redirecting -- defaulting you to the `/draft` version of
  // the `SEMANTIC_PROJECT_WORKBENCH` route
  SEMANTIC_PROJECT_WORKBENCH_BASE: createClientSideRoute({
    clientSide: true,
    path: "/model-workbench/:semanticProjectId",
    getUrl: ({
      semanticProjectId,
    }: {
      semanticProjectId: SemanticProjectId;
    }): string => `/model-workbench/${semanticProjectId}`,
  }),

  // the workbench for viewing/editing context like guides
  CONTEXT_WORKBENCH: createClientSideRoute({
    clientSide: true,
    path: "/context-studio/workbench",
    getUrl: ({ queryParams }: WithQueryParams): string =>
      `/context-studio/workbench${encodeQueryParams(queryParams)}`,
  }),

  DEV_AGENT_CHAT_THREAD: createClientSideRoute({
    clientSide: true,
    path: "/dev/agent-chat-thread/:threadId",
    getUrl: ({ threadId }: { threadId: string }): string =>
      `/dev/agent-chat-thread/${threadId}`,
  }),

  getTabRoute: (view: ViewType): ClientSideRoute<string, HexRouteParams> => {
    switch (view) {
      case "ui":
        return Routes.UI;
      case "split":
        return Routes.SPLIT_SCREEN;
      case "component":
        return Routes.COMPONENT;
      case "logic":
        return Routes.LOGIC;
      case "debug":
        return Routes.DEBUG;
      default:
        return assertNever(view, view);
    }
  },
} satisfies {
  [
    key: string
  ]: /* eslint-disable @typescript-eslint/no-explicit-any -- any params are fine! */
    | ServerSideRoute<any>
    | ClientSideRoute<any, any>
    | ((view: ViewType) => ClientSideRoute<any, any>);
};
